import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import LifecycleBadge from '../components/common/LifecycleBadge';
import { orderApi } from '../api/services';
import toast from 'react-hot-toast';
import { formatDistanceToNow } from 'date-fns';
import { Search, Loader2, Inbox } from 'lucide-react';

const STAGE_FILTERS = [
  { value: '',                     label: 'Any stage' },
  { value: 'intake',               label: 'Intake' },
  { value: 'in_review',            label: 'In review' },
  { value: 'ready_for_dispatch',   label: 'Ready for dispatch' },
  { value: 'with_vendor',          label: 'With vendor' },
  { value: 'in_transit',           label: 'In transit' },
  { value: 'on_hold',              label: 'On hold' },
  { value: 'needs_attention',      label: 'Needs attention' },
  { value: 'data_incomplete',      label: 'Data incomplete' },
  { value: 'reship_unlinked',      label: 'Reship — needs linking' },
  { value: 'blocked_troublesome_customer', label: 'Blocked (troublesome)' },
];

const TEAM_FILTERS = [
  { value: '',                label: 'Any team' },
  { value: 'dispatch_admin',  label: 'Dispatch admin' },
  { value: 'dispatch_user',   label: 'Dispatch user' },
  { value: 'ops',             label: 'Ops' },
  { value: 'vendor',          label: 'Vendor' },
];

export default function OpsAllOrders() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [team, setTeam] = useState('');
  const [stage, setStage] = useState('');

  async function load() {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (team) params.team = team;
      if (stage) params.stage = stage;
      const res = await orderApi.listActive(params);
      setRows(res.data.orders || []);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load');
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="p-6">
      <div className="mb-4">
        <h1 className="text-2xl font-semibold text-gray-900">Search orders</h1>
        <p className="text-sm text-gray-500 mt-1">
          Every active order (not completed or cancelled). Search by order ID or customer name,
          filter by owner team or stage, and open any order to view details or act on it.
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-3 mb-4 flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="block text-xs text-gray-600 mb-1">Search (order ID or customer)</span>
          <input value={search} onChange={(e) => setSearch(e.target.value)}
                 onKeyDown={(e) => e.key === 'Enter' && load()}
                 placeholder="IDM… or Rebecca Thiel"
                 className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm w-60" />
        </label>
        <label className="block">
          <span className="block text-xs text-gray-600 mb-1">Owner team</span>
          <select value={team} onChange={(e) => setTeam(e.target.value)}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm">
            {TEAM_FILTERS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="block text-xs text-gray-600 mb-1">Stage</span>
          <select value={stage} onChange={(e) => setStage(e.target.value)}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm">
            {STAGE_FILTERS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </label>
        <button onClick={load}
                className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
          <Search className="w-4 h-4" /> Search
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-gray-500">
            <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center text-gray-500 text-sm">
            <Inbox className="w-10 h-10 mx-auto mb-3 text-gray-300" />
            No orders match these filters.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="text-left px-3 py-2">Order</th>
                <th className="text-left px-3 py-2">Customer</th>
                <th className="text-left px-3 py-2">City</th>
                <th className="text-left px-3 py-2">Stage</th>
                <th className="text-left px-3 py-2">Owner</th>
                <th className="text-left px-3 py-2">Since</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((r) => (
                <tr key={r.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2 font-mono text-xs">
                    <Link to={`/orders/${r.id}`} className="text-blue-600 hover:underline">
                      {r.order_id_external}
                    </Link>
                    {r.is_reship && <span className="ml-1 text-[10px] bg-purple-100 text-purple-700 px-1 rounded">reship</span>}
                  </td>
                  <td className="px-3 py-2">{r.customer_name}</td>
                  <td className="px-3 py-2 text-xs text-gray-600">{r.shipping_city || '—'}</td>
                  <td className="px-3 py-2"><LifecycleBadge stage={r.lifecycle_stage} ownerTeam={r.current_owner_team} hasReship={r.has_reship} /></td>
                  <td className="px-3 py-2 text-xs text-gray-600">{r.current_owner_team}</td>
                  <td className="px-3 py-2 text-xs text-gray-500">
                    {r.current_owner_since
                      ? formatDistanceToNow(new Date(r.current_owner_since), { addSuffix: true })
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
